import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, ChevronRight, BookOpen, Award } from "lucide-react";
import { CORSI } from "@/data/corsiData";

export default function CorsiInPartenzaPage() {
  return (
    <main id="main-content" data-testid="corsi-partenza-page">

      {/* ── Hero ── */}
      <section className="relative h-[300px] md:h-[360px] overflow-hidden" aria-labelledby="corsi-title">
        <img
          src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1920&q=60&auto=format"
          alt="Formazione professionale"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4,30,66,0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
            Formazione
          </span>
          <h1
            id="corsi-title"
            className="text-4xl md:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight"
          >
            Calendario Corsi
          </h1>
          <p className="mt-3 text-white/70 text-base max-w-xl">
            Percorsi abilitanti e di alta formazione in partenza nelle prossime settimane. Iscriviti subito per assicurarti un posto.
          </p>
        </div>
      </section>

      {/* ── Griglia corsi ── */}
      <section className="py-14 bg-[#F5F8FC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Contatore */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "2.5rem", flexWrap: "wrap", gap: "1rem" }}>
            <div>
              <span
                className="font-['Encode_Sans_Semi_Condensed']"
                style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "var(--confcom-blue)", display: "block", marginBottom: "0.3rem" }}
              >
                Prossime sessioni
              </span>
              <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">
                {CORSI.length} corsi disponibili
              </h2>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 1rem",
                background: "#EBF5FF",
                borderRadius: "0.5rem",
                fontSize: "0.82rem",
                color: "var(--confcom-blue)",
                fontWeight: 600,
              }}
            >
              <BookOpen size={14} />
              Sede: Via Marco e Marcelliano, 45 – Roma
            </div>
          </div>

          {/* Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1.5rem" }}>
            {CORSI.map((corso) => (
              <Link
                key={corso.slug}
                to={`/formazione/corsi-in-partenza/${corso.slug}`}
                data-testid={`corso-card-${corso.slug}`}
                style={{ textDecoration: "none" }}
              >
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid var(--confcom-border)",
                    borderRadius: "1rem",
                    overflow: "hidden",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "box-shadow 0.2s, transform 0.2s",
                  }}
                  className="hover:shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  {/* Immagine */}
                  <div style={{ height: 200, overflow: "hidden", position: "relative", background: "#EEF3F8", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img
                      src={corso.immagine}
                      alt={corso.titolo}
                      style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center" }}
                    />
                    {/* Badge categoria */}
                    <div
                      style={{
                        position: "absolute",
                        top: "0.75rem",
                        left: "0.75rem",
                        background: corso.categoriaColor,
                        color: "#fff",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        padding: "0.3rem 0.7rem",
                        borderRadius: "2rem",
                      }}
                    >
                      {corso.categoria}
                    </div>
                    {/* Badge codice */}
                    <div
                      style={{
                        position: "absolute",
                        top: "0.75rem",
                        right: "0.75rem",
                        background: "rgba(255,255,255,0.92)",
                        color: corso.categoriaColor,
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        padding: "0.3rem 0.7rem",
                        borderRadius: "2rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {corso.codice}
                    </div>
                  </div>

                  {/* Contenuto */}
                  <div style={{ padding: "1.25rem 1.5rem", flex: 1, display: "flex", flexDirection: "column" }}>
                    <h3
                      className="font-['Encode_Sans_Semi_Condensed']"
                      style={{ fontSize: "1rem", fontWeight: 700, color: "var(--confcom-text-primary)", marginBottom: "1rem", lineHeight: 1.3 }}
                    >
                      {corso.titolo}
                      {corso.sottotitolo && (
                        <span style={{ display: "block", fontSize: "0.82rem", fontWeight: 400, color: "var(--confcom-text-secondary)", marginTop: "0.2rem" }}>
                          {corso.sottotitolo}
                        </span>
                      )}
                    </h3>

                    {/* Meta info */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1.25rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.82rem", color: "var(--confcom-text-secondary)" }}>
                        <Calendar size={13} color="var(--confcom-blue)" />
                        <span>Inizio: <strong style={{ color: "var(--confcom-text-primary)" }}>{corso.dataInizio}</strong></span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.82rem", color: "var(--confcom-text-secondary)" }}>
                        <Clock size={13} color="var(--confcom-blue)" />
                        <span>{corso.durata}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.82rem", color: "var(--confcom-text-secondary)" }}>
                        <MapPin size={13} color="var(--confcom-blue)" />
                        <span>{corso.modalita}</span>
                      </div>
                    </div>

                    {/* Quota */}
                    <div
                      style={{
                        background: "#F0F7FF",
                        borderRadius: "0.5rem",
                        padding: "0.6rem 0.85rem",
                        fontSize: "0.82rem",
                        color: "var(--confcom-blue)",
                        fontWeight: 700,
                        marginBottom: "1.25rem",
                      }}
                    >
                      {corso.quota}
                    </div>

                    {/* Accreditamenti badge se presenti */}
                    {corso.accreditamenti && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "1rem" }}>
                        {corso.accreditamenti.map((a, i) => (
                          <span
                            key={i}
                            style={{
                              background: "#F5F3FF",
                              color: "#5B21B6",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              padding: "0.2rem 0.6rem",
                              borderRadius: "2rem",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.25rem",
                            }}
                          >
                            <Award size={10} /> {a}
                          </span>
                        ))}
                      </div>
                    )}

                    <div style={{ marginTop: "auto" }}>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--confcom-blue)] group-hover:gap-2 transition-all">
                        Scopri il corso <ChevronRight size={16} />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA iscrizione ── */}
      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span
            className="font-['Encode_Sans_Semi_Condensed']"
            style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "var(--confcom-blue)", display: "block", marginBottom: "0.5rem" }}
          >
            Hai domande?
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-4">
            Contatta l'Area Formazione
          </h2>
          <p className="text-[var(--confcom-text-secondary)] mb-8">
            Per informazioni sui corsi, sui costi e sulle modalità di iscrizione contatta direttamente i nostri esperti.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "1rem", flexWrap: "wrap" }}>
            <a
              href="tel:0668437462"
              className="inline-flex items-center gap-2 bg-[var(--confcom-blue)] text-white px-7 py-3 rounded-md font-medium hover:opacity-90 transition-opacity"
              style={{ textDecoration: "none" }}
            >
              06.68437462
            </a>
            <a
              href="mailto:promoter@confcommercioroma.it"
              className="inline-flex items-center gap-2 border border-[var(--confcom-blue)] text-[var(--confcom-blue)] px-7 py-3 rounded-md font-medium hover:bg-[#EBF5FF] transition-colors"
              style={{ textDecoration: "none" }}
            >
              promoter@confcommercioroma.it
            </a>
          </div>
        </div>
      </section>

    </main>
  );
}
